import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Typography,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Autocomplete,
  Paper,
  Stack,
  Divider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// ?? usa el paginado para buscar globalmente
import {
  getProductosPaged,
  insertarProducto as insertarProductoApi,
  lookupProductos,
} from "../../../services/modules/Productos";
import { insertarCompra, getCompras, getCompra } from "../../../services/modules/Compras";
import { getProveedores } from "../../../services/modules/Proveedores";
import { getCategorias } from "../../../services/modules/Categorias";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// ---- Helpers ----
const formatGTQ = (n) =>
  `Q${(Number(n) || 0).toFixed(2)}`;

const Compras = () => {
  // Autocomplete (server-side + infinite scroll)
  const [productos, setProductos] = useState([]);
  const [openAuto, setOpenAuto] = useState(false);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const reqSeq = useRef(0);
  const debounceId = useRef(null);

  // Otros estados
  const [proveedores, setProveedores] = useState([]);
  const [proveedorInput, setProveedorInput] = useState("");
  const [precioCompra, setPrecioCompra] = useState("");
  const [detalleCompra, setDetalleCompra] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState("1");
  const [precioVenta, setPrecioVenta] = useState("");
  const [idProveedor, setIdProveedor] = useState("");
  const [noFactura, setNoFactura] = useState("");
  const [precioVentaEditado, setPrecioVentaEditado] = useState(false);

  // Tabs / Reporte
  const [tab, setTab] = useState(0); // 0=Registrar, 1=Reporte
  const [reporteCompras, setReporteCompras] = useState([]);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [downloadingCompraId, setDownloadingCompraId] = useState(null);

  // Modal: Nuevo Producto desde Compras
  const [openNuevoProducto, setOpenNuevoProducto] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [savingProducto, setSavingProducto] = useState(false);
  const [productoNuevo, setProductoNuevo] = useState({
    codigo: "",
    codigo_barras: "",
    descripcion: "",
    id_categoria: "",
    precio_compra: "",
    precio_venta: "",
    modelo: "",
    cantidad_inicial: 0,
    nivel_minimo: 0,
  });

  const excelInputRef = useRef(null);
  const [importingExcel, setImportingExcel] = useState(false);

  // Responsive
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Config Autocomplete
  const MIN_CHARS = 1;
  const PAGE_SIZE = 50;
  const debounceMs = 300;

  // Fecha local en formato yyyy-MM-ddTHH:mm para datetime-local
  const obtenerFechaLocal = () => {
    const ahora = new Date();
    const offsetMs = ahora.getTimezoneOffset() * 60000;
    const localTime = new Date(ahora.getTime() - offsetMs);
    return localTime.toISOString().slice(0, 16);
  };
  const [fecha, setFecha] = useState(obtenerFechaLocal());

  // Cargar proveedores una vez (productos se buscan on-type)
  useEffect(() => {
    (async () => {
      try {
        const proveedoresData = await getProveedores();
        setProveedores(Array.isArray(proveedoresData) ? proveedoresData : []);
      } catch (e) {
        console.error("Error al cargar proveedores:", e);
        setProveedores([]);
      }
    })();
  }, []);

  // Cargar marcas/categor�as para modal en primer uso
  const ensureCategorias = async () => {
    if (categorias.length) return;
    try {
      const data = await getCategorias();
      setCategorias(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar marcas:", e);
    }
  };

  // ---- Buscar p�gina (gen�rica) ----
  const fetchPage = async ({ q, pageIndex }) => {
    const mySeq = ++reqSeq.current;

    if (pageIndex === 0) {
      setLoadingOpciones(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const data = await getProductosPaged({ q, page: pageIndex, pageSize: PAGE_SIZE });
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(data?.total) ? data.total : undefined;

      if (mySeq !== reqSeq.current) return; // evita carreras

      if (pageIndex === 0) {
        setProductos(items);
      } else {
        // concatenar evitando duplicados por id
        setProductos(prev => {
          const seen = new Set(prev.map(p => p.id));
          const nuevos = items.filter(p => !seen.has(p.id));
          return [...prev, ...nuevos];
        });
      }

      setPage(pageIndex);

      // calcular hasMore
      if (typeof total === "number") {
        setHasMore((pageIndex + 1) * PAGE_SIZE < total);
      } else {
        setHasMore(items.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error("Error buscando productos:", err);
      if (pageIndex === 0) setProductos([]);
      setHasMore(false);
    } finally {
      if (mySeq === reqSeq.current) {
        setLoadingOpciones(false);
        setLoadingMore(false);
      }
    }
  };

  // Debounce del texto
  useEffect(() => {
    if (debounceId.current) clearTimeout(debounceId.current);
    const q = (query || "").trim();
    if (q.length < MIN_CHARS) {
      setProductos([]);
      setHasMore(false);
      setPage(0);
      setLoadingOpciones(false);
      setLoadingMore(false);
      return;
    }
    debounceId.current = setTimeout(() => {
      fetchPage({ q, pageIndex: 0 });
    }, debounceMs);
    return () => clearTimeout(debounceId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Infinite scroll: cargar m�s cuando se acerca al final
  const handleListboxScroll = (event) => {
    const el = event.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 64; // umbral
    if (nearBottom && hasMore && !loadingMore && !loadingOpciones) {
      const q = (query || "").trim();
      if (q.length >= MIN_CHARS) {
        fetchPage({ q, pageIndex: page + 1 });
      }
    }
  };

  const agregarDetalle = () => {
    if (!productoSeleccionado) {
      alert("Selecciona un producto.");
      return;
    }
    const cant = parseInt(cantidad, 10);
    const pCompra = parseFloat(precioCompra);
    const pVenta = parseFloat(precioVenta);

    if (!Number.isFinite(cant) || cant <= 0) {
      alert("La cantidad debe ser mayor a 0.");
      return;
    }
    if (!Number.isFinite(pCompra) || pCompra <= 0) {
      alert("El precio de compra debe ser mayor a 0.");
      return;
    }
    if (!Number.isFinite(pVenta) || pVenta <= 0) {
      alert("El precio de venta debe ser mayor a 0.");
      return;
    }

    const producto = productoSeleccionado; // ya viene del server
    const nuevoDetalle = {
      id_producto: producto.id,
      codigo: producto.codigo || "",
      codigo_barras: producto.codigo_barras || "",
      descripcion: producto.descripcion,
      cantidad: cant,
      precio_unitario_compra: pCompra,
      total: +(cant * pCompra).toFixed(2),
      precio_venta: pVenta,
      caducidad: 0
    };

    setDetalleCompra(prev => [...prev, nuevoDetalle]);

    // reset inputs y resultados del autocomplete
    setCantidad("");
    setPrecioVenta("");
    setPrecioCompra("");
    setProductoSeleccionado(null);
    setQuery("");
    setProductos([]);
    setHasMore(false);
    setPage(0);
    setPrecioVentaEditado(false);
  };

  // Modal Nuevo Producto helpers
  const abrirModalNuevoProducto = async () => {
    await ensureCategorias();
    const q = (query || "").trim();
    setProductoNuevo((p) => ({
      ...p,
      codigo: q && !q.includes(" ") ? q : p.codigo,
      descripcion: q || p.descripcion,
    }));
    setOpenNuevoProducto(true);
  };

  const cerrarModalNuevoProducto = () => {
    if (savingProducto) return;
    setOpenNuevoProducto(false);
    setProductoNuevo({
      codigo: "",
      codigo_barras: "",
      descripcion: "",
      id_categoria: "",
      precio_compra: "",
      precio_venta: "",
      modelo: "",
      cantidad_inicial: 0,
      nivel_minimo: 0,
    });
  };

  const guardarNuevoProducto = async () => {
    const codigoOk = String(productoNuevo.codigo || "").trim();
    const descOk = String(productoNuevo.descripcion || "").trim();
    if (!codigoOk || !descOk || !productoNuevo.id_categoria) {
      alert("Código, nombre y marca son obligatorios");
      return;
    }
    try {
      setSavingProducto(true);
      const payload = {
        ...productoNuevo,
        codigo: codigoOk,
        codigo_barras: String(productoNuevo.codigo_barras || "").trim(),
        descripcion: descOk,
      };
      const res = await insertarProductoApi(payload);
      if (!res?.id) throw new Error("Sin id devuelto");

      const cat = categorias.find((c) => String(c.id_categoria) === String(payload.id_categoria));
      const nuevo = {
        id: res.id,
        codigo: payload.codigo,
        codigo_barras: payload.codigo_barras,
        descripcion: payload.descripcion,
        id_categoria: payload.id_categoria,
        categoria: cat?.nombre || "",
        precio_compra: Number(payload.precio_compra) || 0,
        precio_venta: Number(payload.precio_venta) || 0,
        modelo: payload.modelo || "",
        cantidad_inicial: Number(payload.cantidad_inicial) || 0,
        nivel_minimo: Number(payload.nivel_minimo) || 0,
      };
      setProductos((prev) => [nuevo, ...prev]);
      setProductoSeleccionado(nuevo);
      if (nuevo.precio_venta > 0) setPrecioVenta(String(nuevo.precio_venta));
      setPrecioVentaEditado(false);
      setOpenNuevoProducto(false);
    } catch (err) {
      console.error("Error al crear producto:", err);
      alert("No se pudo crear el producto");
    } finally {
      setSavingProducto(false);
    }
  };

  const eliminarDetalle = (index) => {
    setDetalleCompra(prev => prev.filter((_, i) => i !== index));
  };

  const recalcDetalle = (item) => {
    const cantidad = Math.max(0, parseInt(item?.cantidad, 10) || 0);
    const precioCompra = Number(item?.precio_unitario_compra || 0) || 0;
    return {
      ...item,
      cantidad,
      precio_unitario_compra: precioCompra,
      precio_venta: Number(item?.precio_venta || 0) || 0,
      total: +(cantidad * precioCompra).toFixed(2),
    };
  };

  const updateDetalle = (index, patch) => {
    setDetalleCompra((prev) =>
      (prev || []).map((it, i) => (i === index ? recalcDetalle({ ...it, ...patch }) : it))
    );
  };

  const normalizeKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const s = String(value).trim();
    if (!s) return null;
    // soporta "1,234.56" y "1.234,56"
    const normalized = s.includes(",") && !s.includes(".")
      ? s.replace(",", ".")
      : s.replace(/,/g, "");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  };

  const getRowValue = (row, keys) => {
    if (!row || typeof row !== "object") return "";
    for (const k of keys) {
      const direct = row[k];
      if (direct !== undefined && direct !== null && String(direct).trim() !== "") return direct;
    }
    // fallback por normalización
    const normalizedMap = new Map(Object.keys(row).map((k) => [normalizeKey(k), k]));
    for (const key of keys) {
      const originalKey = normalizedMap.get(normalizeKey(key));
      if (!originalKey) continue;
      const v = row[originalKey];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  const handleImportExcel = async (file) => {
    if (!file) return;
    setImportingExcel(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames?.[0];
      if (!sheetName) throw new Error("El archivo no tiene hojas");

      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const parsed = (Array.isArray(rows) ? rows : [])
        .map((r) => {
          const codigo = String(
            getRowValue(r, ["codigo", "cod", "sku", "codigo_producto", "Codigo", "CODIGO"])
          ).trim();
          const codigo_barras = String(
            getRowValue(r, ["codigo_barras", "codigobarras", "barras", "barcode", "ean", "upc"])
          ).trim();

          const qtyRaw = getRowValue(r, ["cantidad", "qty", "cant", "cantidad_compra"]);
          const compraRaw = getRowValue(r, ["precio_compra", "costo", "precio_unitario_compra", "compra"]);
          const ventaRaw = getRowValue(r, ["precio_venta", "venta"]);
          const descRaw = getRowValue(r, ["descripcion", "producto", "nombre"]);

          const cantidad = parseNumber(qtyRaw) ?? 0;
          const precioCompra = parseNumber(compraRaw);
          const precioVenta = parseNumber(ventaRaw);

          return {
            codigo,
            codigo_barras,
            descripcion: String(descRaw || "").trim(),
            cantidad: Number.isFinite(cantidad) ? Math.trunc(cantidad) : 0,
            precioCompra,
            precioVenta,
            hasCompra: String(compraRaw || "").trim() !== "",
            hasVenta: String(ventaRaw || "").trim() !== "",
          };
        })
        .filter((r) => (r.codigo || r.codigo_barras) && (r.cantidad || 0) > 0);

      if (!parsed.length) {
        alert("El Excel no tiene filas válidas. Revisa que existan columnas como codigo/codigo_barras y cantidad.");
        return;
      }

      const codes = parsed.map((r) => r.codigo).filter(Boolean);
      const bars = parsed.map((r) => r.codigo_barras).filter(Boolean);

      const found = await lookupProductos({ codigos: codes, codigos_barras: bars });
      const list = Array.isArray(found) ? found : [];

      const byCodigo = new Map(list.map((p) => [String(p.codigo || "").trim(), p]));
      const byBarras = new Map(list.map((p) => [String(p.codigo_barras || "").trim(), p]));

      const nuevos = [];
      const missing = [];

      parsed.forEach((row) => {
        const prod =
          (row.codigo_barras && byBarras.get(row.codigo_barras)) ||
          (row.codigo && byCodigo.get(row.codigo)) ||
          null;

        if (!prod) {
          missing.push(row.codigo_barras || row.codigo || "(sin codigo)");
          return;
        }

        const precioUnitCompra = row.hasCompra ? (row.precioCompra ?? 0) : Number(prod.precio_compra || 0);
        const precioUnitVenta = row.hasVenta ? (row.precioVenta ?? 0) : Number(prod.precio_venta || 0);
        const cantidad = Number(row.cantidad || 0);

        nuevos.push({
          id_producto: prod.id,
          codigo: prod.codigo || row.codigo || "",
          codigo_barras: prod.codigo_barras || row.codigo_barras || "",
          descripcion: row.descripcion || prod.descripcion,
          cantidad,
          precio_unitario_compra: precioUnitCompra,
          total: +(cantidad * precioUnitCompra).toFixed(2),
          precio_venta: precioUnitVenta,
          caducidad: 0,
          _hasCompra: row.hasCompra,
          _hasVenta: row.hasVenta,
        });
      });

      if (!nuevos.length) {
        alert("No se encontraron productos del Excel en el sistema.");
        return;
      }

      const existingIds = new Set((detalleCompra || []).map((d) => String(d?.id_producto || "")));
      const hasOverlap = nuevos.some((it) => existingIds.has(String(it.id_producto)));
      const mergeMode = hasOverlap
        ? window.confirm(
            "Ya existen productos en el detalle.\n\nAceptar = SUMAR cantidades\nCancelar = REEMPLAZAR las filas"
          )
          ? "sum"
          : "replace"
        : "sum";

      setDetalleCompra((prev) => {
        const out = [...(prev || [])];
        const idxById = new Map(out.map((it, i) => [String(it.id_producto), i]));

        // Consolidar duplicados dentro del mismo import
        const consolidated = new Map();
        nuevos.forEach((it) => {
          const key = String(it.id_producto);
          const prevIt = consolidated.get(key);
          if (!prevIt) {
            consolidated.set(key, it);
            return;
          }

          if (mergeMode === "replace") {
            // última fila gana
            consolidated.set(key, it);
            return;
          }

          // sum: sumar cantidades; precios solo si venían en Excel
          const nextQty = Number(prevIt.cantidad || 0) + Number(it.cantidad || 0);
          consolidated.set(key, {
            ...prevIt,
            cantidad: nextQty,
            precio_unitario_compra: it._hasCompra ? it.precio_unitario_compra : prevIt.precio_unitario_compra,
            precio_venta: it._hasVenta ? it.precio_venta : prevIt.precio_venta,
            total: +(nextQty * Number(it._hasCompra ? it.precio_unitario_compra : prevIt.precio_unitario_compra || 0)).toFixed(2),
            _hasCompra: prevIt._hasCompra || it._hasCompra,
            _hasVenta: prevIt._hasVenta || it._hasVenta,
          });
        });

        Array.from(consolidated.values()).forEach((it) => {
          const key = String(it.id_producto);
          const idx = idxById.get(key);
          const { _hasCompra, _hasVenta, ...clean } = it;

          if (idx === undefined) {
            out.push(clean);
            idxById.set(key, out.length - 1);
            return;
          }

          if (mergeMode === "replace") {
            out[idx] = clean;
            return;
          }

          const existing = out[idx];
          const nextQty = Number(existing.cantidad || 0) + Number(clean.cantidad || 0);
          const nextCompra = _hasCompra ? clean.precio_unitario_compra : existing.precio_unitario_compra;
          const nextVenta = _hasVenta ? clean.precio_venta : existing.precio_venta;

          out[idx] = {
            ...existing,
            ...clean,
            cantidad: nextQty,
            precio_unitario_compra: nextCompra,
            precio_venta: nextVenta,
            total: +(nextQty * Number(nextCompra || 0)).toFixed(2),
          };
        });

        return out;
      });

      const msgMissing = missing.length
        ? `\n\nNo encontrados (${missing.length}):\n- ${missing.slice(0, 10).join("\n- ")}${missing.length > 10 ? "\n..." : ""}`
        : "";

      alert(`Excel importado.\nAgregados: ${nuevos.length}${msgMissing}`);
    } catch (error) {
      console.error("Error importando Excel:", error);
      alert("No se pudo importar el Excel. Verifica el formato del archivo.");
    } finally {
      setImportingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
    }
  };

  const descargarPlantillaExcel = () => {
    const headers = [
      "codigo",
      "codigo_barras",
      "cantidad",
      "precio_compra",
      "precio_venta",
      "descripcion",
    ];

    const sample = [
      {
        codigo: "ABC-001",
        codigo_barras: "",
        cantidad: 5,
        precio_compra: 10,
        precio_venta: "",
        descripcion: "Opcional (si la dejas vacía, usa la del sistema)",
      },
    ];

    const sheet = XLSX.utils.json_to_sheet(sample, { header: headers });
    // Forzar orden de headers
    XLSX.utils.sheet_add_aoa(sheet, [headers], { origin: "A1" });

    const instrucciones = [
      ["Plantilla de importación de compras"],
      ["Campos:"],
      ["- codigo o codigo_barras: (al menos uno) para identificar el producto"],
      ["- cantidad: requerido (>0)"],
      ["- precio_compra: opcional (si vacío, usa el costo actual del sistema)"],
      ["- precio_venta: opcional (si vacío, usa el precio de venta actual del sistema)"],
      ["- descripcion: opcional (si vacío, usa la descripción del sistema)"],
    ];
    const sheet2 = XLSX.utils.aoa_to_sheet(instrucciones);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Plantilla");
    XLSX.utils.book_append_sheet(wb, sheet2, "Instrucciones");

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `plantilla_compra_${date}.xlsx`);
  };

  const buscarCompras = useCallback(async () => {
    setLoadingReporte(true);
    try {
      const data = await getCompras({ fechaInicio, fechaFin, limit: 500 });
      setReporteCompras(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar reporte de compras:", e);
      setReporteCompras([]);
      alert("No se pudo cargar el reporte de compras");
    } finally {
      setLoadingReporte(false);
    }
  }, [fechaInicio, fechaFin]);

  const descargarPdfCompra = async (idCompra) => {
    setDownloadingCompraId(idCompra);
    try {
      const data = await getCompra(idCompra);
      const compra = data?.compra || {};
      const detalles = Array.isArray(data?.detalles) ? data.detalles : [];

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "A4" });
      doc.setFontSize(14);
      doc.text(`Compra #${compra?.id_compra || idCompra}`, 40, 40);
      doc.setFontSize(10);
      if (compra?.fecha) doc.text(`Fecha: ${compra.fecha}`, 40, 62);
      if (compra?.proveedor_nombre) doc.text(`Proveedor: ${compra.proveedor_nombre}`, 40, 76);
      if (compra?.no_factura_compra) doc.text(`No. Factura: ${compra.no_factura_compra}`, 40, 90);
      if (compra?.usuario_nombre || compra?.usuario_username) {
        doc.text(
          `Usuario: ${compra.usuario_nombre || ""}${compra.usuario_username ? ` (${compra.usuario_username})` : ""}`.trim(),
          40,
          104
        );
      }

      const head = [["Producto", "Cantidad", "Precio Compra", "Total"]];
      const body = detalles.map((d) => [
        String(d.descripcion || d.codigo || ""),
        String(d.cantidad_compra ?? 0),
        formatGTQ(d.precio_unitario_compra),
        formatGTQ(d.total_compra),
      ]);

      autoTable(doc, {
        head,
        body,
        startY: 125,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [17, 24, 39] },
      });

      const endY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 125;
      const total = detalles.reduce((sum, it) => sum + (Number(it.total_compra) || 0), 0);
      doc.setFontSize(11);
      doc.text(`Total: ${formatGTQ(total)}`, 40, endY + 24);

      const filename = `compra_${compra?.no_factura_compra || compra?.id_compra || idCompra}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
    } catch (e) {
      console.error("Error al descargar PDF de compra:", e);
      alert("No se pudo generar el PDF de la compra");
    } finally {
      setDownloadingCompraId(null);
    }
  };

  const guardarCompra = async () => {
    if (!idProveedor) {
      alert("Selecciona un proveedor.");
      return;
    }
    if (!noFactura.trim()) {
      alert("Ingresa el número de factura.");
      return;
    }
    if (!detalleCompra.length) {
      alert("Agrega al menos un producto al detalle.");
      return;
    }

    const invalid = (detalleCompra || []).find((d) => {
      const qty = parseInt(d?.cantidad, 10);
      const pc = Number(d?.precio_unitario_compra);
      const pv = Number(d?.precio_venta);
      return !(Number.isFinite(qty) && qty > 0 && Number.isFinite(pc) && pc > 0 && Number.isFinite(pv) && pv > 0);
    });
    if (invalid) {
      alert("Hay filas inválidas en el detalle. Revisa cantidad, precio compra y precio venta (deben ser > 0).");
      return;
    }

    const compra = {
      fecha, // yyyy-MM-ddTHH:mm
      no_factura_compra: noFactura,
      id_proveedor: idProveedor,
      detalles: detalleCompra
    };

    const res = await insertarCompra(compra);
    // Generar PDF evidencia basado en los datos ingresados
    try {
      const prov = proveedores.find((p) => String(p.id_proveedor) === String(idProveedor));
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "A4" });
      doc.setFontSize(14);
      doc.text("Evidencia de Ingreso de Compra", 40, 40);
      doc.setFontSize(10);
      doc.text(`Fecha: ${fecha}`, 40, 62);
      doc.text(`Proveedor: ${prov?.nombre || idProveedor}`, 40, 76);
      if (noFactura) doc.text(`No. Factura: ${noFactura}`, 40, 90);

      const head = [["Producto", "Cantidad", "Precio Compra", "Precio Venta", "Total"]];
      const body = (detalleCompra || []).map((d) => [
        String(d.descripcion || ""),
        String(d.cantidad || 0),
        `Q${(Number(d.precio_unitario_compra) || 0).toFixed(2)}`,
        `Q${(Number(d.precio_venta) || 0).toFixed(2)}`,
        `Q${(Number(d.total) || 0).toFixed(2)}`,
      ]);
      autoTable(doc, { head, body, startY: 110, styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: [17, 24, 39] } });
      const endY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 110;
      const totalQ = calcularTotal();
      doc.setFontSize(11);
      doc.text(`Total: Q${(Number(totalQ) || 0).toFixed(2)}`, 40, endY + 24);
      const filename = `compra_${(noFactura || "sin_factura")}_${new Date().toISOString().slice(0,10)}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.warn("No se pudo generar PDF de compra:", err);
    }
    alert(res?.message || "Compra guardada.");

    // limpiar todo (incluye autocomplete)
    setDetalleCompra([]);
    setIdProveedor("");
    setNoFactura("");
    setFecha(obtenerFechaLocal());
    setProductoSeleccionado(null);
    setQuery("");
    setProductos([]);
    setHasMore(false);
    setPage(0);
    setCantidad(1);
    setPrecioVenta("");
    setPrecioCompra("");
  };

  const calcularTotal = () =>
    detalleCompra.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

  useEffect(() => {
    if (tab !== 1) return;
    if (loadingReporte) return;
    if (reporteCompras.length) return;
    buscarCompras();
  }, [tab, loadingReporte, reporteCompras.length, buscarCompras]);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>Compras</Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Registrar" />
        <Tab label="Reporte" />
      </Tabs>

      {tab === 0 ? (
        <>
          <Typography variant="h6" gutterBottom>Registrar Compra</Typography>

          <Grid container spacing={2} mt={0}>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            options={proveedores}
            value={
              proveedores.find((prov) => String(prov.id_proveedor) === String(idProveedor)) || null
            }
            onChange={(_, newValue) => setIdProveedor(newValue?.id_proveedor || "")}
            inputValue={proveedorInput}
            onInputChange={(_, value) => setProveedorInput(value)}
            isOptionEqualToValue={(option, value) =>
              String(option.id_proveedor) === String(value?.id_proveedor)
            }
            getOptionLabel={(option) =>
              option ? `${option.nombre || "Sin nombre"} • NIT ${option.nit || "CF"}` : ""
            }
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (q.length < 2) return [];
              return options.filter((opt) =>
                [opt.nombre, opt.nit]
                  .filter(Boolean)
                  .some((field) => field.toLowerCase().includes(q))
              );
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id_proveedor}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {option.nombre || "Sin nombre"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    NIT: {option.nit || "CF"}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Proveedor"
                placeholder="Buscar por nombre o NIT"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="No. Factura"
            value={noFactura}
            onChange={(e) => setNoFactura(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Fecha"
            type="datetime-local"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Cantidad"
            type="number"
            value={cantidad}
            onChange={(e) => {
              setCantidad(e.target.value);
            }}
            error={(() => {
              const val = cantidad;
              if (val === "" || val === null || val === undefined) return true;
              const n = parseInt(val, 10);
              return !Number.isFinite(n) || n <= 0;
            })()}
            helperText={(() => {
              const val = cantidad;
              if (val === "" || val === null || val === undefined) return "Cantidad requerida";
              const n = parseInt(val, 10);
              if (!Number.isFinite(n)) return "Ingresa un número válido";
              if (n <= 0) return "Debe ser mayor a 0";
              return undefined;
            })()}
            fullWidth
          />
        </Grid>

        

        

        <Grid item xs={12} sm={6}>
          <Autocomplete
            // ?? s�lo abrir si hay al menos 1 caracter
            open={openAuto && query.trim().length >= MIN_CHARS}
            onOpen={() => setOpenAuto(true)}
            onClose={() => setOpenAuto(false)}

            // opciones vienen del servidor; no filtramos en cliente
            options={Array.isArray(productos) ? productos : []}
            filterOptions={(opts) => opts}

            loading={loadingOpciones || loadingMore}
            loadingText={loadingMore ? "Cargando..." : "Buscando..."}
            noOptionsText={ query.trim().length < MIN_CHARS ? ("Escribe para buscar...") : (<Box sx={{ px: 1, py: 0.5 }}><Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="body2">Sin coincidencias</Typography><Button size="small" variant="outlined" onMouseDown={(e) => e.preventDefault()} onClick={abrirModalNuevoProducto}>Agregar producto</Button></Stack></Box>) }

            getOptionLabel={(option) => {
              if (!option || typeof option !== "object") return "";
              const cod = option.codigo ? `[${option.codigo}] ` : "";
              const barras = option.codigo_barras ? `[${option.codigo_barras}] ` : "";
              const modelo = option.modelo || "";
              const cat = option.categoria || "";
              const desc = option.descripcion || "";
              return [cod + barras + modelo, cat, desc].filter(Boolean).join(" - ");
            }}
            isOptionEqualToValue={(opt, val) => !!opt && !!val && opt.id === val.id}

            value={productoSeleccionado}
            onChange={(_, newValue) => {
              setProductoSeleccionado(newValue);
              const prevVenta = Number(newValue?.precio_venta);
              if (Number.isFinite(prevVenta) && prevVenta > 0) {
                setPrecioVenta(String(prevVenta));
                setPrecioVentaEditado(false);
              } else {
                setPrecioVenta("");
                setPrecioVentaEditado(false);
              }
            }}

            inputValue={query}
            onInputChange={(_, newInputValue) => setQuery(newInputValue)}

            // INFINITE SCROLL
            ListboxProps={{ onScroll: (e) => handleListboxScroll(e) }}

            renderInput={(params) => (
              <TextField
                {...params}
                label="Producto (escribe código, modelo, marca o descripción)"
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Precio Compra"
            type="number"
            value={precioCompra}
            onChange={(e) => {
              const val = e.target.value;
              setPrecioCompra(val);
              const n = parseFloat(val);
              if (Number.isFinite(n) && n > 0) {
                const tienePrevVenta = Number(productoSeleccionado?.precio_venta) > 0;
                if (!tienePrevVenta && !precioVentaEditado) {
                  const sugerido = (n * 1.5).toFixed(2);
                  setPrecioVenta(sugerido);
                }
              }
            }}
            fullWidth
            helperText={(() => {
              const prev = Number(productoSeleccionado?.precio_compra);
              const n = Number(precioCompra);
              if (Number.isFinite(prev) && prev > 0) {
                if (Number.isFinite(n) && n > prev) {
                  return `Alerta: costo subió (antes Q${prev.toFixed(2)})`;
                }
                return `Costo anterior: Q${prev.toFixed(2)}`;
              }
              return undefined;
            })()}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            label="Precio Venta"
            type="number"
            value={precioVenta}
            onChange={(e) => { setPrecioVenta(e.target.value); setPrecioVentaEditado(true); }}
            fullWidth
            helperText={(() => {
              const prevV = Number(productoSeleccionado?.precio_venta);
              if (Number.isFinite(prevV) && prevV > 0) {
                return `Venta anterior: Q${prevV.toFixed(2)}`;
              }
              return undefined;
            })()}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={agregarDetalle}
            disabled={
              !productoSeleccionado ||
              !precioCompra ||
              !precioVenta ||
              !cantidad ||
              !(parseInt(cantidad, 10) > 0)
            }
          >
            Agregar
          </Button>
        </Grid>
      </Grid>

      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Detalle de Compra</Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => handleImportExcel(e.target.files?.[0])}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={importingExcel}
            onClick={() => excelInputRef.current?.click()}
          >
            {importingExcel ? "Importando..." : "Importar Excel"}
          </Button>
          <Button variant="outlined" onClick={descargarPlantillaExcel}>
            Descargar plantilla
          </Button>
          <Button variant="text" disabled={!detalleCompra.length} onClick={() => setDetalleCompra([])}>
            Limpiar detalle
          </Button>
        </Stack>

        {/* M�vil: tarjetas */}
        {isXs ? (
          <Box>
            {detalleCompra.length === 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                <Typography align="center" color="text.secondary">Sin Items</Typography>
              </Paper>
            )}

            {detalleCompra.map((item, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 1.5, borderRadius: 2, boxShadow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Box sx={{ fontWeight: 700 }}>
                      {item.descripcion}
                      {item.codigo ? ` (${item.codigo})` : ""}
                      {!item.codigo && item.codigo_barras ? ` (${item.codigo_barras})` : ""}
                    </Box>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={4}>
                        <TextField
                          label="Cant."
                          size="small"
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => updateDetalle(idx, { cantidad: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Compra"
                          size="small"
                          type="number"
                          value={item.precio_unitario_compra}
                          onChange={(e) => updateDetalle(idx, { precio_unitario_compra: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Venta"
                          size="small"
                          type="number"
                          value={item.precio_venta}
                          onChange={(e) => updateDetalle(idx, { precio_venta: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Descripción"
                          size="small"
                          value={item.descripcion}
                          onChange={(e) => updateDetalle(idx, { descripcion: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <IconButton color="error" onClick={() => eliminarDetalle(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                  <Typography variant="caption" fontWeight={700}>{formatGTQ(item.total)}</Typography>
                </Stack>
              </Paper>
            ))}

            {detalleCompra.length > 0 && (
              <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Total:</Typography>
                  <Typography variant="subtitle2" color="error" fontWeight={700}>
                    {formatGTQ(calcularTotal())}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        ) : (
          // Desktop: tabla con cabecera negra y bordes
          <TableContainer component={Paper}>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Codigo</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Cantidad</TableCell>
                <TableCell>Precio Compra</TableCell>
                <TableCell>Precio Venta</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Eliminar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalleCompra.map((item, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.codigo || item.codigo_barras || "-"}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.descripcion}
                      onChange={(e) => updateDetalle(idx, { descripcion: e.target.value })}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.cantidad}
                      onChange={(e) => updateDetalle(idx, { cantidad: e.target.value })}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: 160 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.precio_unitario_compra}
                      onChange={(e) => updateDetalle(idx, { precio_unitario_compra: e.target.value })}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ width: 160 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.precio_venta}
                      onChange={(e) => updateDetalle(idx, { precio_venta: e.target.value })}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>{formatGTQ(item.total)}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => eliminarDetalle(idx)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {detalleCompra.length > 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="right">
                    <strong>Total:</strong>
                  </TableCell>
                  <TableCell colSpan={2}>
                    <Typography variant="h5" color="error" fontWeight="bold">
                      {formatGTQ(calcularTotal())}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {detalleCompra.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Sin Items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box mt={3}>
        <Button variant="contained" color="success" onClick={guardarCompra}>
          Guardar Compra
        </Button>
      </Box>

        </>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>Reporte de Compras</Typography>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="contained" onClick={buscarCompras} disabled={loadingReporte}>
              {loadingReporte ? <CircularProgress size={18} /> : "Buscar"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Factura</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(reporteCompras || []).map((c) => (
                  <TableRow key={c.id_compra} hover>
                    <TableCell>{c.fecha}</TableCell>
                    <TableCell>{c.no_factura_compra || "-"}</TableCell>
                    <TableCell>{c.proveedor_nombre || c.id_proveedor || "-"}</TableCell>
                    <TableCell>{c.usuario_nombre || c.usuario_username || "-"}</TableCell>
                    <TableCell align="right">{formatGTQ(c.total_compra)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => descargarPdfCompra(c.id_compra)}
                        disabled={downloadingCompraId === c.id_compra}
                        color="primary"
                      >
                        {downloadingCompraId === c.id_compra ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(!reporteCompras || reporteCompras.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Sin compras para mostrar
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Modal: Nuevo Producto desde Compras */}
      <Dialog open={openNuevoProducto} onClose={savingProducto ? undefined : cerrarModalNuevoProducto} fullWidth maxWidth="md">
        <DialogTitle>Registrar Producto Rápido</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Código"
                value={productoNuevo.codigo}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, codigo: e.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Codigo de barras"
                value={productoNuevo.codigo_barras}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, codigo_barras: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre del producto"
                value={productoNuevo.descripcion}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, descripcion: e.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Marca"
                select
                value={productoNuevo.id_categoria}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, id_categoria: e.target.value }))}
                required
                fullWidth
              >
                {categorias.map((cat) => (
                  <MenuItem key={cat.id_categoria} value={cat.id_categoria}>
                    {cat.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Precio de Venta"
                type="number"
                value={productoNuevo.precio_venta}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, precio_venta: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Precio de Compra"
                type="number"
                value={productoNuevo.precio_compra}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, precio_compra: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Modelo"
                value={productoNuevo.modelo}
                onChange={(e) => setProductoNuevo((p) => ({ ...p, modelo: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModalNuevoProducto} disabled={savingProducto}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={guardarNuevoProducto} disabled={savingProducto}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Compras;
